import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { ThemeContext } from "./context/ThemeContext";
import api from "../api/api";

const searchURL = (value) => {
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

const ProcurementPage = () => {
  const { theme } = useContext(ThemeContext);
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [analysedDays, setAnalysedDays] = useState(90);
  const [filterText, setFilterText] = useState("");
  const [days, setDays] = useState(90);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const requestVersion = useRef(0);
  const pending = useRef(false);

  const isDark =
    theme.sidebarColor &&
    !theme.sidebarColor.toLowerCase().includes("#fff") &&
    !theme.sidebarColor.toLowerCase().includes("#f");

  const cardBg = isDark ? "#2a2d2f" : "#fff";
  const textColor = isDark ? "#e0e0e0" : "#222";
  const subTextColor = isDark ? "#aaa" : "#666";

  const loadRecommendations = async () => {
    if (pending.current) return;
    pending.current = true;
    const version = ++requestVersion.current;
    const requestedDays = Math.max(7, Math.min(365, Math.floor(Number(days) || 90)));
    setDays(requestedDays);
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(
        `/procurement/recommendations?days=${requestedDays}&low_stock_threshold=2`,
      );
      if (version !== requestVersion.current) return;
      if (!res.data || (res.data.recommendations != null && !Array.isArray(res.data.recommendations))) throw new Error('Некорректные данные анализа');
      setRecommendations((res.data.recommendations || []).filter(item => item && typeof item === 'object'));
      setTotalCount(res.data.total || 0);
      setAnalysedDays(res.data.analysed_days || requestedDays);
      setGeneratedAt(res.data.generated_at && !Number.isNaN(new Date(res.data.generated_at).getTime()) ? res.data.generated_at : null);
    } catch (e) {
      if (version !== requestVersion.current) return;
      setError("Ошибка загрузки рекомендаций. Попробуйте ещё раз.");
    } finally {
      if (version === requestVersion.current) {
        pending.current = false;
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadRecommendations();
    return () => { requestVersion.current += 1; pending.current = false; };
    // eslint-disable-next-line
  }, []);

  const filtered = recommendations.filter(
    (r) =>
      String(r.name || "").toLowerCase().includes(filterText.toLowerCase()) ||
      String(r.category || "").toLowerCase().includes(filterText.toLowerCase()),
  );

  const priorityConfig = {
    high: {
      label: "Срочно",
      color: "#d32f2f",
      bgColor: "#ffebee",
      icon: <ErrorOutlineIcon fontSize="small" />,
    },
    medium: {
      label: "Рекомендуется",
      color: "#f57c00",
      bgColor: "#fff3e0",
      icon: <WarningAmberIcon fontSize="small" />,
    },
    low: {
      label: "Запас",
      color: "#388e3c",
      bgColor: "#e8f5e9",
      icon: <CheckCircleOutlineIcon fontSize="small" />,
    },
  };

  return (
    <Box sx={{ p: 3, color: textColor }}>
      {/* Заголовок */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <ShoppingCartIcon sx={{ fontSize: 32, color: "#1976d2" }} />
        <Box>
          <Typography variant="h5" fontWeight={700} color={textColor}>
            Закупки
          </Typography>
          <Typography variant="body2" color={subTextColor}>
            ИИ-анализ активов — что нужно закупить исходя из спроса и остатков
          </Typography>
        </Box>
      </Box>

      {/* Панель управления */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          label="Поиск по названию"
          size="small"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 260 }}
        />
        <TextField
          label="Период анализа (дней)"
          size="small"
          type="number"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          sx={{ width: 180 }}
          inputProps={{ min: 7, max: 365 }}
        />
        <Button
          variant="contained"
          onClick={loadRecommendations}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <RefreshIcon />
            )
          }
        >
          Обновить анализ
        </Button>
        {generatedAt && (
          <Typography variant="caption" color={subTextColor}>
            Обновлено: {new Date(generatedAt).toLocaleString("ru-RU")}
          </Typography>
        )}
      </Box>

      {/* Сводка */}
      {!loading && (
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <Chip
            icon={<ErrorOutlineIcon />}
            label={`Срочно: ${recommendations.filter((r) => r.priority === "high").length}`}
            sx={{ bgcolor: "#ffebee", color: "#d32f2f", fontWeight: 600 }}
          />
          <Chip
            icon={<WarningAmberIcon />}
            label={`Рекомендуется: ${recommendations.filter((r) => r.priority === "medium").length}`}
            sx={{ bgcolor: "#fff3e0", color: "#f57c00", fontWeight: 600 }}
          />
          <Chip
            label={`Анализ за ${analysedDays} дней`}
            variant="outlined"
            size="small"
          />
        </Box>
      )}

      {/* Ошибка */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Загрузка */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Пусто */}
      {!loading && !error && filtered.length === 0 && (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 64, color: "#4caf50", mb: 2 }}
          />
          <Typography variant="h6" color={subTextColor}>
            {filterText
              ? "Ничего не найдено по фильтру"
              : "Все активы в норме — закупать ничего не нужно!"}
          </Typography>
        </Box>
      )}

      {/* Карточки рекомендаций */}
      {!loading && (
        <Grid container spacing={2}>
          {filtered.map((item, idx) => {
            const pc = priorityConfig[item.priority] || priorityConfig.medium;
            const avitoURL = searchURL(item.search_urls?.avito);
            const ozonURL = searchURL(item.search_urls?.ozon);
            return (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card
                  sx={{
                    bgcolor: cardBg,
                    border: `2px solid ${pc.color}`,
                    borderRadius: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    {/* Приоритет */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          color: pc.color,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {pc.icon}
                      </Box>
                      <Chip
                        label={pc.label}
                        size="small"
                        sx={{
                          bgcolor: pc.bgColor,
                          color: pc.color,
                          fontWeight: 700,
                          fontSize: 11,
                        }}
                      />
                      {item.category && (
                        <Chip
                          label={item.category}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 10 }}
                        />
                      )}
                    </Box>

                    {/* Название */}
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      color={textColor}
                      sx={{ mb: 1 }}
                    >
                      {item.name}
                    </Typography>

                    {/* Статистика */}
                    <Box
                      sx={{ display: "flex", gap: 2, mb: 1, flexWrap: "wrap" }}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color="#d32f2f"
                        >
                          {item.free_units}
                        </Typography>
                        <Typography variant="caption" color={subTextColor}>
                          свободных
                        </Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color={textColor}
                        >
                          {item.total_units}
                        </Typography>
                        <Typography variant="caption" color={subTextColor}>
                          всего
                        </Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color="#1976d2"
                        >
                          {item.rental_count_period}
                        </Typography>
                        <Typography variant="caption" color={subTextColor}>
                          аренд за {item.analysed_days}д
                        </Typography>
                      </Box>
                    </Box>

                    {/* Причина */}
                    <Typography
                      variant="caption"
                      color={subTextColor}
                      sx={{ display: "block", mb: 2 }}
                    >
                      {item.reason}
                    </Typography>
                  </CardContent>

                  {/* Кнопки поиска */}
                  <Box
                    sx={{
                      px: 2,
                      pb: 2,
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Tooltip title="Искать на Авито">
                      <Button disabled={!avitoURL}
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<OpenInNewIcon fontSize="small" />}
                        onClick={() =>
                          window.open(avitoURL, "_blank", "noopener,noreferrer")
                        }
                        sx={{ fontSize: 12 }}
                      >
                        Авито
                      </Button>
                    </Tooltip>
                    <Tooltip title="Искать на Ozon">
                      <Button disabled={!ozonURL}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<OpenInNewIcon fontSize="small" />}
                        onClick={() =>
                          window.open(ozonURL, "_blank", "noopener,noreferrer")
                        }
                        sx={{ fontSize: 12 }}
                      >
                        Ozon
                      </Button>
                    </Tooltip>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default ProcurementPage;
